import { useState } from "react";
import { Button, ButtonProps } from "@chakra-ui/react";
import { CollecticonDownload2 } from "@devseed-ui/collecticons-chakra";

import { InferenceCollection } from "../types/inferences";
import { getSceneInferences } from "../api/inferences";
import { generateCsvDownloadUrl } from "../utils/csvExport";

type DownloadDetectionsProps = {
  inferences: InferenceCollection | undefined;
  fileName: string;
  // When provided, fetches all (unfiltered) individual inferences for the scene
  // plus composite info for enrichment. When absent, downloads inferences as-is.
  sceneId?: string;
} & ButtonProps;

// Builds a lookup from individual inference ID → composite fields,
// using the list of member IDs that each composite feature carries.
function buildCompositeMap(compositeFeatures: InferenceCollection["features"]) {
  const map: Record<number, { composite_id: number; composite_count: number; notes: string | null; selected_class: string | null; flagged: boolean }> = {};

  compositeFeatures.forEach(composite => {
    const { composite_id, composite_count, inference_ids, ids, notes, selected_class, flagged } = composite.properties;
    const memberIds = ids || inference_ids || [];
    const count = composite_count ?? memberIds.length ?? 1;

    memberIds.forEach(id => {
      if (composite_id !== undefined) {
        map[id] = { composite_id, composite_count: count, notes: notes ?? null, selected_class: selected_class ?? null, flagged: flagged ?? false };
      }
    });
  });

  return map;
}

// Stamps composite fields onto each individual inference that appears in the
// composite map. Notes and selected_class fall back to the composite's value
// when absent on the individual inference (they are stored at the composite level).
function enrichWithComposites(
  data: InferenceCollection,
  compositeMap: ReturnType<typeof buildCompositeMap>
): InferenceCollection {
  return {
    ...data,
    features: data.features.map(inference => {
      const compositeInfo = inference.properties.id
        ? compositeMap[inference.properties.id]
        : undefined;

      if (!compositeInfo) return inference;

      return {
        ...inference,
        properties: {
          ...inference.properties,
          composite_id: compositeInfo.composite_id,
          composite_count: compositeInfo.composite_count,
          notes: inference.properties.notes ?? compositeInfo.notes,
          selected_class: inference.properties.selected_class ?? compositeInfo.selected_class,
          flagged: inference.properties.flagged || compositeInfo.flagged,
        }
      };
    })
  };
}

function DownloadDetections({
  children,
  inferences,
  fileName,
  sceneId,
  ...props
}: DownloadDetectionsProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownloadClick = async () => {
    if (!inferences) return;

    setIsLoading(true);
    try {
      let mergedData: InferenceCollection;

      if (sceneId) {
        // Fetch all inferences for the scene unfiltered, alongside composites for enrichment.
        // Ignore any UI filters here so the download always contains the full scene record.
        const [individualData, compositeData] = await Promise.all([
          getSceneInferences(sceneId, { composited: false }),
          getSceneInferences(sceneId, { composited: true }),
        ]);
        mergedData = enrichWithComposites(individualData, buildCompositeMap(compositeData.features));
      } else {
        // Multi-scene path: individual inferences are already in the prop.
        // Fetch composites per scene in parallel to build the enrichment map.
        const sceneIds = Array.from(new Set(inferences.features.map(f => f.properties.scene.s3_prefix)));
        const compositeResults = await Promise.allSettled(
          sceneIds.map(id => getSceneInferences(id, { composited: true }))
        );
        // Skip scenes that errored (e.g. 500s) and proceed with the ones that succeeded
        const compositeFeatures = compositeResults
          .filter((r): r is PromiseFulfilledResult<InferenceCollection> => r.status === "fulfilled")
          .flatMap(r => r.value.features);
        const compositeMap = buildCompositeMap(compositeFeatures);
        mergedData = enrichWithComposites(inferences, compositeMap);
      }

      const downloadUrl = generateCsvDownloadUrl(mergedData);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching complementary data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      rightIcon={<CollecticonDownload2 />}
      onClick={handleDownloadClick}
      isLoading={isLoading}
      {...props}
    >
      {children}
    </Button>
  );
}

export default DownloadDetections;
